import {
  buildAppLoginRedirectPath,
  getAppAuthRedirectFromRoute,
} from "~/utils/appAuthRedirect";

export default defineNuxtRouteMiddleware(async (to) => {
  // Only apply to /app routes, but exclude /app/login
  // Also exclude /fr/app routes (they should not exist, but handle them just in case)
  if (
    !to.path.startsWith("/app") ||
    to.path === "/app/login" ||
    to.path === "/app/legacy/login" ||
    to.path.startsWith("/fr/app")
  ) {
    return;
  }

  // Skip SSR: OAuth hash tokens (#access_token) are only available client-side
  if (import.meta.server) return;

  console.log("🔐 Middleware: Checking auth for path:", to.path);

  try {
    const supabase = useSupabaseClient();

    // Normalize path (remove trailing slash)
    const normalizedPath = to.path.replace(/\/$/, "") || to.path;

    // Check if user is authenticated
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    // Always use /app/login (no language prefix for /app routes)
    const loginPath = buildAppLoginRedirectPath(
      getAppAuthRedirectFromRoute(to),
    );

    // The current Atelier is served directly at /app.
    if (normalizedPath === "/app") {
      if (authError || !user) {
        console.log("🔐 Middleware: No user, redirecting /app to login");
        return navigateTo(loginPath, { replace: true });
      }
      return;
    }

    // For other /app routes, check authentication
    if (authError) {
      console.error("🔐 Middleware: Auth error:", authError);
      return navigateTo(loginPath, { replace: true });
    }

    if (!user) {
      console.log("🔐 Middleware: No user, redirecting to login");
      return navigateTo(loginPath, { replace: true });
    }

    console.log("🔐 Middleware: User found:", user.email);

    // Check if user is authorized
    // For /app/backoffice, require 'admin' role
    const requiredRole =
      normalizedPath === "/app/backoffice" ? "admin" : undefined;

    let response;
    try {
      const bearerHeaders = await getBearerHeaders();
      response = await $fetch("/api/auth-check", {
        method: "POST",
        headers: bearerHeaders,
        body: {
          email: user.email,
          requiredRole: requiredRole,
        },
      });
    } catch (error) {
      console.error("🔐 Middleware: Auth check error:", error);
      // On error, redirect to login for security
      await supabase.auth.signOut();
      return navigateTo(loginPath, { replace: true });
    }

    if (!response || !response.authorized) {
      console.log("🔐 Middleware: User not authorized, signing out");
      console.log("🔐 Middleware: Reason:", response?.message || "Unknown");
      // Sign out unauthorized user and redirect to login
      await supabase.auth.signOut();
      return navigateTo(loginPath, { replace: true });
    }

    console.log("🔐 Middleware: User authorized, sending login alert");

    // Send login alert email for authorized access
    // L'IP sera récupérée automatiquement dans l'API send-login-alert
    $fetch("/api/send-login-alert", {
      method: "POST",
      body: {
        userEmail: user.email,
        userAgent: "Server-side middleware",
        timestamp: new Date().toISOString(),
      },
    })
      .then((emailResult) => {
        console.log(
          "🔐 Middleware: Login alert sent successfully:",
          emailResult,
        );
      })
      .catch((emailError) => {
        console.error("🔐 Middleware: Failed to send login alert:", emailError);
        // Don't block access if email fails
      });
  } catch (error) {
    // If any unexpected error occurs, redirect to login for security
    console.error("🔐 Middleware: Unexpected error:", error);
    return navigateTo(
      buildAppLoginRedirectPath(getAppAuthRedirectFromRoute(to)),
      { replace: true },
    );
  }
});
